import { useState } from "react";
import { useUser } from "../../../../../shared/context/UserContext";
import api from "../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import { GetConfigurationPageSetup } from "../../../../services/services";
import { useBusinessNavigation } from "./useBusinessNavigation";
import useUsers from "src/business-types/grocery/hooks/useUsers";

export interface SubscriptionStatusResponse {
  expiry_date?: string;
  grace_end_date?: string;
  message?: string;
  status: "active" | "inactive" | "expired" | "grace_period" | "blocked";
  warning?: string | null;
  daysRemaining?: number;
}

export const useAuth = (userName: string, password: string, setIsLoginDisabled: (val: boolean) => void, setemployee: (val: boolean) => void) => {
  const [Loading, setLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const { setConfigurations, setSubscriptionInfo } = useUser();
  const { reload } = useUsers()
  const { navigateByBusiness } = useBusinessNavigation();

  const handleLogin = async () => {
    setLoading(true);
    setIsLoginDisabled(true);
    setSubscriptionError(null);

    try {
      const formData = new FormData();
      formData.append("user_name", userName);
      formData.append("password", password);

      const response = await api.post(`/pos/v2/login`, formData);
      if (response.status !== 200) throw new Error("Login failed");
      const { token, user } = response.data;

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const subscriptionRes = await api.get("/app/pos/v2/subscription");
      const subscription = subscriptionRes.data as SubscriptionStatusResponse;

      if (subscription.status === "blocked") {
        delete api.defaults.headers.common["Authorization"];

        setSubscriptionError(subscription.message || "Your subscription has expired. Please contact POS support.");

        setIsLoginDisabled(false);
        setLoading(false);
        return;
      }

      sessionStorage.setItem("jsonwebtoken", token);

      setSubscriptionInfo(subscription);

      await handleSuccessfulLogin(user);
    } catch (error: any) {
      console.error("Login error:", error);

      if (error.response) {
        const status = error.response.status;
        const backendMessage = error.response.data?.message;

        if (status === 401) {
          notifyError("Invalid username or password.");
        } else {
          setSubscriptionError(backendMessage || "Subscription error. Please contact support.");
        }
      } else if (error.request) {
        notifyError("No response from server. Check internet connection.");
      } else {
        notifyError("Unexpected login error.");
      }

      setIsLoginDisabled(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = async (user: any) => {
    try {
      reload()
      const configRes = await GetConfigurationPageSetup();

      if (configRes?.data?.result?.[0]) {
        setConfigurations(configRes.data.result[0]);
      }

      const businessType = user?.[0]?.business_type;

      sessionStorage.setItem("business_type", businessType);

      notifySuccess("Logged in successfully");

      setTimeout(() => {
        navigateByBusiness(businessType, "");
      }, 1000);
    } catch {
      notifyError("Error loading user data.");
    }
  };

  return {
    handleLogin,
    Loading,
    subscriptionError,
  };
};
